import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { DefaultNoIndex } from "@/components/SEO";
import { LanguageUrlSync } from "@/components/LanguageUrlSync";
import { LanguagePersistenceGuard } from "@/components/LanguagePersistenceGuard";
import { initAnalytics } from "@/lib/analytics";
import { usePageviews } from "@/hooks/usePageviews";
import { Toaster } from "@/components/ui/sonner";

const LoginDialog = lazy(() =>
  import("@/components/auth/LoginDialog").then((m) => ({ default: m.LoginDialog }))
);
const ForgotPasswordDialog = lazy(() =>
  import("@/components/auth/ForgotPasswordDialog").then((m) => ({ default: m.ForgotPasswordDialog }))
);

const AuthDialogs = () => {
  const { loginDialogOpen } = useAuth();
  const [forgotOpened, setForgotOpened] = useState(false);

  useEffect(() => {
    const handler = () => setForgotOpened(true);
    window.addEventListener("open-forgot-password", handler);
    return () => window.removeEventListener("open-forgot-password", handler);
  }, []);

  return (
    <Suspense fallback={null}>
      {loginDialogOpen && <LoginDialog />}
      {forgotOpened && <ForgotPasswordDialog onClose={() => setForgotOpened(false)} />}
    </Suspense>
  );
};

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;

    let observer: MutationObserver | undefined;
    const scrollToLocation = () => {
      observer?.disconnect();
      const rawId = window.location.hash.slice(1);
      if (!rawId) {
        window.scrollTo(0, 0);
        return;
      }

      let id = rawId;
      try {
        id = decodeURIComponent(rawId);
      } catch {
        // A malformed fragment can still match a literal element id.
      }
      const scrollToTarget = () => {
        const target = document.getElementById(id);
        if (!target) return false;
        target.scrollIntoView({ block: "start" });
        observer?.disconnect();
        return true;
      };

      if (!scrollToTarget()) {
        // Lazy pages can render their anchor after the layout effect runs.
        observer = new MutationObserver(scrollToTarget);
        observer.observe(document.body, { childList: true, subtree: true });
      }
    };

    scrollToLocation();
    window.addEventListener("hashchange", scrollToLocation);
    return () => {
      observer?.disconnect();
      window.removeEventListener("hashchange", scrollToLocation);
    };
  }, [pathname, hash]);
  return null;
}

// Mounted inside LanguageProvider so `usePageviews` can read `useLanguage()`.
// `initAnalytics()` is idempotent; safe to fire on every Layout mount.
function Analytics() {
  useEffect(() => {
    initAnalytics();
  }, []);
  usePageviews();
  return null;
}

/**
 * Root layout for all routes - mounts the shared storefront providers and
 * persistent chrome (navbar, footer, dialogs, toasts, cookie banner).
 *
 * The `<Outlet />` renders whichever teaser or SPA-only page matched the
 * URL. When vite-react-ssg prerenders a teaser path, this tree is
 * walked once per path with the correct `useLocation()` so
 * `LanguageContext` + `<SEO>` resolve the right language.
 */
export const Layout = () => (
  <CurrencyProvider>
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <Toaster />
          <DefaultNoIndex />
          <ScrollToTop />
          <Analytics />
          <LanguageUrlSync />
          <LanguagePersistenceGuard />
          <AuthDialogs />
          <div className="flex flex-col min-h-screen">
            <Navbar />
            <div className="flex-1" id="content-outlet" tabIndex={-1}>
              <Outlet />
            </div>
            <Footer />
            <CookieBanner />
          </div>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  </CurrencyProvider>
);

export default Layout;
